import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  Folder,
  FolderCog,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react'
import { type FormEvent, Fragment, useEffect, useState } from 'react'
import {
  BulkActionBar,
  EmptyTable,
  GroupHeaderRow,
  Pager,
  RowCheckbox,
  SelectAllCheckbox,
  SortableHeader,
  TableToolbar,
} from '../components/data-table'
import { EntityAccessPanel } from '../components/entity-access-panel'
import { ManualGroupPanel } from '../components/manual-group-panel'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { TableShell } from '../components/ui/table-shell'
import { api, type Product } from '../lib/api'
import { useDataTable } from '../lib/data-table'
import { formatCurrency } from '../lib/format'
import { firstLetterGroup, type GroupOption, useGroupedTable } from '../lib/grouped-table'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

const PRODUCT_GROUP_OPTIONS: GroupOption[] = [
  { key: 'none', label: 'Keine' },
  // Product has no explicit category column today — fall back to A/B/C…
  // bins so the dropdown is still useful for at-a-glance scanning.
  { key: 'category', label: 'Kategorie' },
]

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products',
  component: ProductsPage,
})

export function ProductsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [dialogState, setDialogState] = useState<{ open: boolean; product: Product | null }>({
    open: false,
    product: null,
  })
  // Mobile-Shell (<640): Suche-Sheet hinterm Bottom-Slot; Titel nur
  // standalone — eingebettet (/stammdaten-Tab) gehört er der Host-Seite.
  const [searchSheet, setSearchSheet] = useState(false)
  useMobilePageTitle(embedded ? null : 'Produkte')
  const [accessPanel, setAccessPanel] = useState<{
    open: boolean
    product: Product | null
    bulkIds: string[] | null
  }>({ open: false, product: null, bulkIds: null })
  const [groupPanel, setGroupPanel] = useState<
    { open: boolean; mode: 'assign'; ids: string[] } | { open: boolean; mode: 'manage' }
  >({ open: false, mode: 'manage' })
  const [showArchived, setShowArchived] = useState(false)

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['products', { archived: showArchived }],
    queryFn: () => api.listProducts(showArchived),
    enabled: !!meData?.user,
  })
  const { data: groupsData } = useQuery({
    queryKey: ['groups', 'product'],
    queryFn: () => api.listEntityGroups('product'),
    enabled: !!meData?.user,
  })

  const products = data?.products ?? []
  const productGroups = groupsData?.groups ?? []
  const isAdminUser = meData?.user?.role === 'admin'

  const table = useDataTable<Product>(
    products,
    {
      name: 'name',
      price: 'defaultPriceCents',
      unit: 'unit',
    },
    {
      searchableFields: ['name', 'description'],
      defaultSortKey: 'name',
      defaultSortDir: 'asc',
      defaultPageSize: 25,
      storageKey: 'products',
    },
  )

  const grouped = useGroupedTable<Product>(table, {
    pageKey: 'products',
    options: PRODUCT_GROUP_OPTIONS,
    manualGroups: productGroups,
    manualGroupsEnabled: isAdminUser,
    getId: (p) => p.id,
    groupBy: (p, mode) => {
      if (mode === 'category') {
        // The Product schema has no dedicated `category` column yet, so
        // fall back to first-letter buckets. Once a category field is
        // added (see SPEC), swap the read path here.
        return firstLetterGroup(p.name)
      }
      return null
    },
  })

  const queryClient = useQueryClient()
  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteProducts(ids),
    onSuccess: (r) => {
      table.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['products'] })
      notify.success(`${r.deleted} Produkte gelöscht`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  const isAdmin = meData.user.role === 'admin'

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche → Sheet + ＋ Neues Produkt —
          Handler identisch zu den Kopf-/Toolbar-Controls darunter. */}
      <MobileBottomSlots>
        <BarSlot
          label="Suche"
          icon={<Search className="h-5 w-5" />}
          active={table.search !== ''}
          ariaLabel="Produkte durchsuchen"
          ariaHasPopup
          onClick={() => setSearchSheet(true)}
        />
        <BarAccentPlus
          ariaLabel="Neues Produkt"
          onClick={() => setDialogState({ open: true, product: null })}
        />
      </MobileBottomSlots>

      {/* Suche-Sheet (<640): bindet dieselbe table.search wie die
          Toolbar-Suche — Treffer filtern live hinter dem Sheet. */}
      <SidePanel open={searchSheet} onClose={() => setSearchSheet(false)} title="Suche">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={table.search}
            onChange={(e) => table.setSearch(e.target.value)}
            placeholder="Produkte durchsuchen …"
            className="pl-8"
            autoFocus
          />
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Produkte"
        actions={
          <Button onClick={() => setDialogState({ open: true, product: null })} size="lg">
            <Plus className="h-4 w-4" /> Neues Produkt
          </Button>
        }
      />

      <TableToolbar
        search={table.search}
        onSearch={table.setSearch}
        filteredRows={table.filteredRows}
        totalRows={table.totalRows}
        pageSize={table.pageSize}
        onPageSize={table.setPageSize}
        groupBy={{
          value: grouped.groupBy,
          options: grouped.options,
          onChange: grouped.setGroupBy,
          onExpandAll: grouped.expandAll,
          onCollapseAll: grouped.collapseAll,
          allCollapsed: grouped.allCollapsed,
        }}
        rightSlot={
          <>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setGroupPanel({ open: true, mode: 'manage' })}
                title="Gruppen verwalten"
                className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-text-tertiary hover:text-accent hover:bg-bg-overlay/40 transition-colors"
              >
                <FolderCog className="h-3.5 w-3.5" />
                <span className="text-xs whitespace-nowrap">Gruppen verwalten</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs text-text-tertiary hover:text-accent transition-colors whitespace-nowrap"
            >
              {showArchived ? 'Aktive zeigen' : 'Archivierte zeigen'}
            </button>
          </>
        }
      />

      <TableShell className="rounded-2xl bg-bg-elevated/40">
        {isLoading ? (
          <div className="px-6 py-12 text-center text-text-secondary">Lade …</div>
        ) : products.length === 0 ? (
          <EmptyTable message="Noch keine Produkte. Lege z.B. „SSL-Zertifikat 1 Jahr“ mit 89 € an." />
        ) : table.rows.length === 0 ? (
          <EmptyTable message="Keine Treffer." hint="Suche oder Filter anpassen." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bg-overlay/40">
              <tr className="text-left">
                {isAdmin && (
                  <th className="pl-5 pr-2 py-2.5 w-10">
                    <SelectAllCheckbox
                      checked={table.allOnPageSelected}
                      indeterminate={table.someOnPageSelected}
                      onToggle={table.toggleAllOnPage}
                    />
                  </th>
                )}
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Name"
                    sortKey="name"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5 w-32">
                  <SortableHeader
                    label="Einheit"
                    sortKey="unit"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5 w-36">
                  <SortableHeader
                    label="Preis"
                    sortKey="price"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                    align="right"
                  />
                </th>
                <th className="px-5 py-2.5 w-px" aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {grouped.disabled
                ? table.rows.map((p) => (
                    <ProductRow
                      key={p.id}
                      product={p}
                      isAdmin={isAdmin}
                      selected={table.isSelected(p.id)}
                      onSelect={() => table.toggleRow(p.id)}
                      onEdit={() => setDialogState({ open: true, product: p })}
                      onManageAccess={() =>
                        setAccessPanel({ open: true, product: p, bulkIds: null })
                      }
                    />
                  ))
                : grouped.groups.map((g) => {
                    const ids = g.items.map((p) => p.id)
                    const selectedInGroup = ids.filter((id) => table.isSelected(id)).length
                    const groupChecked = selectedInGroup > 0 && selectedInGroup === ids.length
                    const groupIndeterminate = selectedInGroup > 0 && selectedInGroup < ids.length
                    return (
                      <Fragment key={g.key}>
                        <GroupHeaderRow
                          label={g.label}
                          count={g.items.length}
                          restrictedCount={g.restrictedCount}
                          collapsed={g.collapsed}
                          onToggle={() => grouped.toggleGroup(g.key)}
                          colSpan={isAdmin ? 5 : 4}
                          groupChecked={isAdmin ? groupChecked : undefined}
                          groupIndeterminate={isAdmin ? groupIndeterminate : undefined}
                          color={g.color}
                          muted={g.muted}
                          onToggleAll={
                            isAdmin
                              ? () => {
                                  const shouldClear =
                                    selectedInGroup === ids.length && ids.length > 0
                                  for (const id of ids) {
                                    const isOn = table.isSelected(id)
                                    if (shouldClear && isOn) table.toggleRow(id)
                                    else if (!shouldClear && !isOn) table.toggleRow(id)
                                  }
                                }
                              : undefined
                          }
                        />
                        {!g.collapsed &&
                          g.items.map((p) => (
                            <ProductRow
                              key={p.id}
                              product={p}
                              isAdmin={isAdmin}
                              selected={table.isSelected(p.id)}
                              onSelect={() => table.toggleRow(p.id)}
                              onEdit={() => setDialogState({ open: true, product: p })}
                              onManageAccess={() =>
                                setAccessPanel({ open: true, product: p, bulkIds: null })
                              }
                            />
                          ))}
                      </Fragment>
                    )
                  })}
            </tbody>
          </table>
        )}
      </TableShell>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />

      {isAdmin && (
        <BulkActionBar
          selectionCount={table.selectionCount}
          filteredRows={table.filteredRows}
          onClear={table.clearSelection}
          onSelectAllFiltered={table.selectAllFiltered}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setGroupPanel({
                open: true,
                mode: 'assign',
                ids: Array.from(table.selectedIds),
              })
            }
          >
            <Folder className="h-3.5 w-3.5" /> Zur Gruppe
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setAccessPanel({
                open: true,
                product: null,
                bulkIds: Array.from(table.selectedIds),
              })
            }
          >
            <Lock className="h-3.5 w-3.5" /> Freigabe
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={bulkDelete.isPending}
            onClick={async () => {
              const ids = Array.from(table.selectedIds)
              const ok = await confirm({
                title: `${ids.length} Produkte löschen?`,
                description: 'Diese Aktion ist unumkehrbar.',
                variant: 'danger',
                confirmLabel: 'Endgültig löschen',
              })
              if (ok) bulkDelete.mutate(ids)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </Button>
        </BulkActionBar>
      )}

      <ProductFormDialog
        open={dialogState.open}
        product={dialogState.product}
        onClose={() => setDialogState({ open: false, product: null })}
      />
      <EntityAccessPanel
        open={accessPanel.open}
        onClose={() => setAccessPanel({ open: false, product: null, bulkIds: null })}
        resourceType="product"
        resourceId={accessPanel.product?.id ?? null}
        resourceIds={accessPanel.bulkIds ?? undefined}
        resourceName={
          accessPanel.bulkIds
            ? `${accessPanel.bulkIds.length} Produkte`
            : (accessPanel.product?.name ?? '')
        }
      />
      {isAdmin &&
        (groupPanel.mode === 'assign' ? (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="product"
            mode="assign"
            resourceIds={groupPanel.ids}
          />
        ) : (
          <ManualGroupPanel
            open={groupPanel.open}
            onClose={() => setGroupPanel({ open: false, mode: 'manage' })}
            resourceType="product"
            mode="manage"
          />
        ))}
    </div>
  )
}

function ProductRow({
  product,
  isAdmin,
  selected,
  onSelect,
  onEdit,
  onManageAccess,
}: {
  product: Product
  isAdmin: boolean
  selected: boolean
  onSelect: () => void
  onEdit: () => void
  onManageAccess: () => void
}) {
  const queryClient = useQueryClient()
  const archive = useMutation({
    mutationFn: () => api.updateProduct(product.id, { archived: !product.archived }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      notify.success(product.archived ? 'Produkt reaktiviert' : 'Produkt archiviert')
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const remove = useMutation({
    mutationFn: () => api.deleteProduct(product.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      notify.success('Produkt gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const price = formatCurrency(product.defaultPriceCents)

  return (
    <tr
      className={`group transition-colors hover:bg-bg-overlay/40 ${
        product.archived ? 'opacity-60' : ''
      } ${selected ? 'bg-accent/5' : ''}`}
    >
      {isAdmin && (
        <td className="pl-5 pr-2 py-3">
          <RowCheckbox checked={selected} onToggle={onSelect} label={`${product.name} auswählen`} />
        </td>
      )}
      <td className="px-5 py-3 align-top">
        <p
          className="flex items-center gap-1.5 text-sm font-medium text-text-primary truncate"
          title={product.name}
        >
          {product.restricted && (
            <span title="Beschränkter Zugriff" className="shrink-0">
              <Lock className="h-3 w-3 text-accent" aria-label="Beschränkter Zugriff" />
            </span>
          )}
          <span className="truncate">{product.name}</span>
        </p>
        {product.description && (
          <p className="mt-0.5 text-xs text-text-tertiary truncate" title={product.description}>
            {product.description}
          </p>
        )}
      </td>
      <td className="px-5 py-3 text-sm text-text-secondary">{product.unit}</td>
      <td className="px-5 py-3 text-right font-mono text-sm text-accent tabular-nums whitespace-nowrap">
        {price}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onEdit}
            aria-label="Bearbeiten"
            className="text-text-tertiary hover:text-accent"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onManageAccess}
              aria-label="Freigabe verwalten"
              title="Freigabe verwalten"
              className={
                product.restricted
                  ? 'text-accent hover:text-accent'
                  : 'text-text-tertiary hover:text-accent'
              }
            >
              <Lock className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => archive.mutate()}
            aria-label={product.archived ? 'Reaktivieren' : 'Archivieren'}
            className="text-text-tertiary hover:text-accent"
          >
            {product.archived ? (
              <ArchiveRestore className="h-4 w-4" />
            ) : (
              <Archive className="h-4 w-4" />
            )}
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={async () => {
                const ok = await confirm({
                  title: 'Produkt löschen',
                  description: `„${product.name}“ wirklich löschen?`,
                  variant: 'danger',
                  confirmLabel: 'Löschen',
                })
                if (ok) remove.mutate()
              }}
              aria-label="Löschen"
              className="text-text-tertiary hover:text-danger"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

function ProductFormDialog({
  open,
  product,
  onClose,
}: {
  open: boolean
  product: Product | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const isEdit = !!product
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [unit, setUnit] = useState('Stück')
  const [tax, setTax] = useState('')

  useEffect(() => {
    if (!open) return
    if (product) {
      setName(product.name)
      setDescription(product.description ?? '')
      setPrice((product.defaultPriceCents / 100).toFixed(2))
      setUnit(product.unit)
      setTax(product.defaultTaxRatePercent ?? '')
    } else {
      setName('')
      setDescription('')
      setPrice('')
      setUnit('Stück')
      setTax('')
    }
  }, [product, open])

  const save = useMutation({
    mutationFn: () => {
      const cents = price ? Math.round(Number(price) * 100) : 0
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        defaultPriceCents: cents,
        unit: unit.trim() || 'Stück',
        defaultTaxRatePercent: tax ? Number(tax) : null,
      }
      if (isEdit && product) return api.updateProduct(product.id, payload)
      return api.createProduct(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      notify.success(isEdit ? 'Produkt aktualisiert' : 'Produkt angelegt')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    save.mutate()
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={isEdit ? 'Produkt bearbeiten' : 'Neues Produkt'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={!name.trim() || save.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="pr-name">Name</Label>
          <Input
            id="pr-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. SSL-Zertifikat 1 Jahr"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pr-desc">Beschreibung (optional)</Label>
          <textarea
            id="pr-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Wird beim Hinzufügen zur Rechnung in die Positions-Beschreibung übernommen."
            className="flex w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3.5 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-y min-h-[64px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="pr-price">Preis (€)</Label>
            <Input
              id="pr-price"
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="89.00"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pr-unit">Einheit</Label>
            <Select id="pr-unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
              <option value="Stück">Stück</option>
              <option value="Pauschal">Pauschal</option>
              <option value="Lizenz">Lizenz</option>
              <option value="Monat">Monat</option>
              <option value="Jahr">Jahr</option>
              <option value="Tag">Tag</option>
              <option value="km">km</option>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="pr-tax">MwSt-Satz % (optional, sonst Rechnungs-Default)</Label>
          <Input
            id="pr-tax"
            type="number"
            min="0"
            step="0.5"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            placeholder="z.B. 7 oder 19"
          />
        </div>
      </form>
    </SidePanel>
  )
}
